import { ChevronDownIcon, IconSize } from '@protoss/ui';
import * as React from 'react';
import { formatLogTs } from '../../../lib/datetime';
import type { JobLogEntry } from '../types/jobs';

function LevelDot({ level }: { level: JobLogEntry['level'] }) {
  return <span className={`jl-dot jl-dot--${level.toLowerCase()}`} aria-hidden="true" />;
}

function LogStepRow({ entry }: { entry: JobLogEntry }) {
  const [open, setOpen] = React.useState(false);
  const hasMetadata = !!entry.metadata && Object.keys(entry.metadata).length > 0;

  return (
    <div className={`jl-row jl-row--${entry.level.toLowerCase()}`}>
      <button
        type="button"
        className={['jl-row__header', hasMetadata ? 'jl-row__header--clickable' : '']
          .filter(Boolean)
          .join(' ')}
        onClick={
          hasMetadata
            ? () => {
                setOpen((o) => !o);
              }
            : undefined
        }
        aria-expanded={hasMetadata ? open : undefined}
      >
        <LevelDot level={entry.level} />
        <span className="jl-row__msg">{entry.message}</span>
        <span className="jl-row__ts">{formatLogTs(entry.timestamp)}</span>
        {hasMetadata && (
          <ChevronDownIcon
            width={IconSize.xs}
            height={IconSize.xs}
            aria-hidden="true"
            className="jl-row__chevron"
            data-expanded={open ? 'true' : 'false'}
          />
        )}
      </button>
      {open && hasMetadata && (
        <div className="jl-row__meta">
          {Object.entries(entry.metadata!).map(([k, v]) => (
            <React.Fragment key={k}>
              <span className="jl-row__meta-key">{k}</span>
              <span className="jl-row__meta-val">{String(v)}</span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

interface JobLogViewerProps {
  logs: JobLogEntry[];
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  /** 'card' (default) wraps logs in a bordered card. 'inline' renders bare entries with no wrapper styling. */
  variant?: 'card' | 'inline';
}

export function JobLogViewer({
  logs,
  hasMore,
  loading,
  onLoadMore,
  variant = 'card',
}: JobLogViewerProps) {
  const rootClass =
    variant === 'inline' ? 'job-log-viewer job-log-viewer--inline' : 'job-log-viewer';

  if (loading && logs.length === 0) {
    return <div className={`${rootClass} job-log-viewer--loading`}>Loading logs…</div>;
  }

  if (!loading && logs.length === 0) {
    return <div className={`${rootClass} job-log-viewer--empty`}>No logs captured.</div>;
  }

  return (
    <div className={rootClass} role="log" aria-label="Job log output" aria-live="polite">
      <div className="job-log-viewer__scroll">
        {logs.map((entry) => (
          <LogStepRow key={entry.id} entry={entry} />
        ))}
      </div>
      {hasMore && (
        <div className="job-log-viewer__more">
          <button type="button" className="job-log-viewer__more-btn" onClick={onLoadMore}>
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
